import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseAxios from 'src/utils/baseAxios';

const parseMaybeString = (d) => (typeof d === 'string' ? JSON.parse(d) : d);

// /api/GetDefinedValues
export const getDefinedValues = createAsyncThunk(
    'consent/get-defined-values',
    async (_, { rejectWithValue }) => {
        try {
            const res = await baseAxios.get('/GetDefinedValues');
            return parseMaybeString(res.data);
        } catch (err) {
            const msg = err?.response?.data?.Message || err.message || 'Unknown error';
            return rejectWithValue({ message: msg });
        }
    }
);

// /api/GetDefinedValuesDetails/{id}
export const getDefinedValuesDetails = createAsyncThunk(
    'consent/get-defined-values-details',
    async (id, { rejectWithValue }) => {
        try {
            const res = await baseAxios.get(`/GetDefinedValuesDetails/${id}`);
            return { id, data: parseMaybeString(res.data) };
        } catch (err) {
            const msg = err?.response?.data?.Message || err.message || 'Unknown error';
            return rejectWithValue({ message: msg });
        }
    }
);

export const fetchConsentMetadata = createAsyncThunk(
    'consent-metadata',
    async ({ consumerId }, { rejectWithValue }) => {
        try {
            const response = await baseAxios.get(`/Consent/consent-metadata/${consumerId}`);
            return response.data;
        }
        catch (err) {
            // Response data varsa (success: false ama data ile)
            if (err.response && err.response.data) {
                const errorData = err.response.data;
                // Data içinde LegalEntities varsa onu koruyalım
                if (errorData.Data && errorData.Data.LegalEntities) {
                    return rejectWithValue({
                        message: errorData.Message || 'Unknown error',
                        data: errorData.Data,
                        success: errorData.Success
                    });
                }
                return rejectWithValue({
                    message: errorData.Message || 'Unknown error',
                    success: errorData.Success
                });
            }
            return rejectWithValue({ message: err.message || 'Unknown error' });
        }
    }
);

export const submitConsent = createAsyncThunk(
    'consent-submit',
    async ({ data }, { rejectWithValue }) => {
        try {
            const response = await baseAxios.post('/Consent/submit', data);
            return response.data;
        } catch (err) {
            if (err.response) {
                const msg = err.response.data.Message || err.response.data.message || err.message || 'Unknown error';
                const errors = err.response.data.errors || [];
                return rejectWithValue({ message: msg, errors });
            }
            return rejectWithValue({ message: err.message || 'Unknown error' });
        }
    }
);

export const updateLegalEntity = createAsyncThunk(
    'consent-update-legal-entity',
    async ({ consumerId, tenantId, selected }, { rejectWithValue }) => {
        try {
            const res = await baseAxios.post('/Consent/update-legal-entity', {
                consumerId,
                tenantId,
                selected, // [1] | [2] | [1,2] - selected LegalEntityId values
            });
            return res.data;
        } catch (err) {
            const msg = err?.response?.data?.Message || err.message || 'Unknown error';
            return rejectWithValue({ message: msg });
        }
    }
);

const initialState = {
    legalEntities: [],
    contacts: { Email: [], Phone: [] },
    consentTypes: [],
    permissionTypes: [],
    identifier: {},
    loading: false,
    error: null,
    submitSuccess: false,
    fetchMetaDataError: null,
    success: null, // Backend'den gelen Success değeri
    definedValues: [],
    definedValuesById: {},
    dvLoading: false,
    dvError: null,
    dvdLoading: false,
    dvdError: null,
    errors: [], // Backend'den gelen detaylı hata mesajları için
};

const consentSlice = createSlice({
    name: 'consent',
    initialState,
    reducers: {
        clearSubmitStatus(state) {
            state.submitSuccess = false;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchConsentMetadata.pending, (state) => {
                state.loading = true;
                state.fetchMetaDataError = null;
                state.success = null;
            })
            .addCase(fetchConsentMetadata.fulfilled, (state, action) => {
                const data = action.payload?.Data || {};
                state.legalEntities = data.LegalEntities || [];
                state.contacts = data.Contacts || { Email: [], Phone: [] };
                state.consentTypes = data.ConsentTypes || [];
                state.permissionTypes = data.PermissionTypes || [];
                state.identifier = data.Identifier || {};
                state.loading = false;
                state.fetchMetaDataError = null;
                state.success = action.payload?.Success;
            })
            .addCase(fetchConsentMetadata.rejected, (state, action) => {
                state.loading = false;
                state.fetchMetaDataError = action.payload?.message;
                state.success = action.payload?.success ?? false; // Backend'den gelen success değeri veya varsayılan false

                // Eğer reject edilirken data varsa (LegalEntities için), onu set et
                if (action.payload?.data) {
                    const data = action.payload.data;
                    state.legalEntities = data.LegalEntities || [];
                    // Diğer alanlar null gelirse varsayılan değerleri koru
                    state.contacts = data.Contacts || { Email: [], Phone: [] };
                    state.consentTypes = data.ConsentTypes || [];
                    state.permissionTypes = data.PermissionTypes || [];
                    state.identifier = data.Identifier || {};
                }

                if (!action.payload?.data && state.legalEntities.length > 0) {
                    state.legalEntities = [];
                }
            })
            // YENİ: Submit durumları
            .addCase(submitConsent.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.submitSuccess = false;
            })
            .addCase(submitConsent.fulfilled, (state, action) => {
                state.loading = false;
                state.submitSuccess = true;
            })
            .addCase(submitConsent.rejected, (state, action) => {
                console.log(action);
                state.loading = false;
                state.error = action.payload?.message;
                state.errors = action.payload?.errors || [];
                state.submitSuccess = false;
            })
            .addCase(updateLegalEntity.pending, (s) => { s.loading = true; s.error = null; })
            .addCase(updateLegalEntity.fulfilled, (s) => { s.loading = false; s.submitSuccess = true; })
            .addCase(updateLegalEntity.rejected, (s, a) => { s.loading = false; s.error = a.payload?.message; })
            .addCase(getDefinedValues.pending, (s) => { s.dvLoading = true; s.dvError = null; })
            .addCase(getDefinedValues.fulfilled, (s, a) => {
                s.dvLoading = false;
                s.definedValues = Array.isArray(a.payload) ? a.payload : (a.payload?.Data ?? []);
            })
            .addCase(getDefinedValues.rejected, (s, a) => { s.dvLoading = false; s.dvError = a.payload?.message; })
            .addCase(getDefinedValuesDetails.pending, (s) => { s.dvdLoading = true; s.dvdError = null; })
            .addCase(getDefinedValuesDetails.fulfilled, (s, a) => {
                s.dvdLoading = false;
                const { id, data } = a.payload || {};
                if (id != null) s.definedValuesById[id] = data;
            })
            .addCase(getDefinedValuesDetails.rejected, (s, a) => { s.dvdLoading = false; s.dvdError = a.payload?.message; });

    }
});

export const consentActions = consentSlice.actions;
export const consentThunks = {
    fetchConsentMetadata,
    submitConsent,
    updateLegalEntity,
    getDefinedValues,
    getDefinedValuesDetails, // <-- yeni thunk
};

export default consentSlice.reducer;
